import React, { useState } from 'react';
import {Mail, Phone, MapPin, Clock,  MessageCircle, FileText, Users, Zap, ChevronRight, Send, CheckCircle} from 'lucide-react';
import './ContactandSupport.css';
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

import { db } from "../firebase";

const ContactandSupport = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  setSubmitStatus(null);

  try {
    await addDoc(collection(db, "contact_messages"), {
      ...formData,
      timestamp: serverTimestamp(),
      status: "unread"
    });

    setSubmitStatus("success");
    setFormData({ name: '', email: '', subject: '', category: '', message: '' });
  } catch (error) {
    console.error("Error sending message:", error);
    setSubmitStatus("error");
  } finally {
    setIsSubmitting(false);
  }
};

  const supportCategories = [
    {
      icon: <FileText className="cs-icon" />,
      title: "Documentation",
      description: "Comprehensive guides and platform references",
      link: "#"
    },
    {
      icon: <MessageCircle className="cs-icon" />,
      title: "Live Chat",
      description: "Real-time support 24/7",
      link: "#",
      badge: "Online"
    },
    {
      icon: <Users className="cs-icon" />,
      title: "Refund Policy",
      description: "Understand how refunds are handled for your plan",
      link: "/refund-policy"
    },
    {
      icon: <Zap className="cs-icon" />,
      title: "FAQ",
      description: "Find answers to common questions about our service",
      link: "/faq"
    }
  ];

  const contactMethods = [
    {
      icon: <Mail className="cs-small-icon" />,
      title: "Email Support",
      detail: "support@nyleads.ai",
      response: "Response within 4 hours"
    },
    {
      icon: <Phone className="cs-small-icon" />,
      title: "Phone Support",
      detail: "+1 (212) 555-0198",
      response: "Mon-Fri, 9AM-6PM EST"
    },
    {
      icon: <MapPin className="cs-small-icon" />,
      title: "Office Location",
      detail: "123 Broadway, Suite 400",
      response: "New York, NY 10006"
    },
  ];

  return (
    <div className="contact-support">
      {/* Hero Section */}
      <div className="cs-hero-section">
        <div className="cs-container">
          <div className="cs-hero-content">
            <h1 className="cs-hero-title">Contact & Support</h1>
            <p className="cs-hero-description">
              Get the help you need, when you need it. Our support team is here to assist you 24/7.
            </p>
          </div>
        </div>
      </div>

      <div className="cs-container cs-main-content">
        {/* Support Categories */}
        <div className="cs-support-categories">
          <h2 className="cs-section-title">How can we help you?</h2>
          <div className="cs-categories-grid">
            {supportCategories.map((category, index) => (
              <div key={index} className="cs-category-card">
                <div className="cs-category-header">
                  <div className="cs-category-icon">
                    {category.icon}
                  </div>
                  {category.badge && (
                    <span className="cs-badge">
                      {category.badge}
                    </span>
                  )}
                </div>
                <h3 className="cs-category-title">
                  {category.title}
                </h3>
                <p className="cs-category-description">
                  {category.description}
                </p>
                <div className="cs-category-link">
                  Learn more
                  <ChevronRight className="cs-chevron-icon" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="cs-content-grid">
          {/* Contact Form */}
          <div className="cs-form-section">
            <h3 className="cs-form-title">Send us a message</h3>
            
            {submitStatus === "success" ? (
              <div className="cs-success-message">
                <CheckCircle className="cs-success-icon" />
                <h4 className="cs-success-title">
                  Message sent successfully!
                </h4>
                <p className="cs-success-description">
                  We'll get back to you within 4 hours.
                </p>
              </div>
            ) : (
              <div className="cs-form-container">
                 {submitStatus === "error" && (
    <div className="cs-error-message">
      ❌ Failed to send message. Please try again.
    </div>
  )}
                <div className="cs-form-row">
                  <div className="cs-form-group">
                    <label className="cs-form-label">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="cs-form-input"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="cs-form-group">
                    <label className="cs-form-label">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="cs-form-input"
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="cs-form-group">
                  <label className="cs-form-label">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="cs-form-input"
                    placeholder="How can we help you?"
                    required
                  />
                </div>

                <div className="cs-form-group">
                  <label className="cs-form-label">
                    Category
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="cs-form-input"
                    required
                  >
                    <option value="">Select a category</option>
                    <option value="technical">Technical Support</option>
                    <option value="billing">Billing & Payments</option>
                    <option value="leads">Lead Data Inquiry</option>
                    <option value="general">General Inquiry</option>
                    <option value="feedback">Feedback</option>
                    <option value="partnership">Partnership</option>
                  </select>
                </div>

                <div className="cs-form-group">
                  <label className="cs-form-label">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    className="cs-form-textarea"
                    placeholder="Please describe your question or issue in detail..."
                    required
                  />
                </div>

                <button
  onClick={handleSubmit}
  className="cs-submit-button"
  disabled={isSubmitting}
>
  <Send className="cs-button-icon" />
  {isSubmitting ? "Sending..." : "Send Message"}
</button>
              </div>
            )}
          </div>

          {/* Contact Information */}
          <div className="cs-contact-info">
            <h3 className="cs-contact-title">Get in touch</h3>
            <p className="cs-contact-description">
              Choose the best way to reach us. Our dedicated support team is ready to assist you with any questions or concerns about your NYC restaurant leads.
            </p>

            <div className="cs-contact-methods">
              {contactMethods.map((method, index) => (
                <div key={index} className="cs-contact-method">
                  <div className="cs-method-icon">
                    {method.icon}
                  </div>
                  <div className="cs-method-content">
                    <h4 className="cs-method-title">
                      {method.title}
                    </h4>
                    <p className="cs-method-detail">
                      {method.detail}
                    </p>
                    <p className="cs-method-response">
                      {method.response}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* FAQ Link */}
            <div className="cs-faq-section">
              <h4 className="cs-faq-title">
                Frequently Asked Questions
              </h4>
              <p className="cs-faq-description">
                Find quick answers to common questions in our comprehensive FAQ section.
              </p>
              <button className="cs-faq-link">
                Browse FAQ
                <ChevronRight className="cs-faq-chevron" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="cs-footer-cta">
        <div className="cs-container cs-footer-content">
          <h2 className="cs-footer-title">
            Still need help?
          </h2>
          <p className="cs-footer-description">
            Our enterprise support team is available for priority assistance and dedicated account management for high-volume lead buyers.
          </p>
          <button className="cs-footer-button">
            Contact Enterprise Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactandSupport;